import { useEffect, useState } from 'react';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { StatusCard } from '@/components/ui/card/StatusCard';
import { useTranslation } from '@/hooks/useTranslation';
import type { AdminApiClient } from '@/features/admin/api/AdminApiClient';

export type ResetPasswordDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  email: string;
  client: AdminApiClient;
  onSuccess: () => void;
};

export function ResetPasswordDialog({ open, onOpenChange, email, client, onSuccess }: ResetPasswordDialogProps) {
  const { t } = useTranslation();
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setPassword('');
      setError(null);
      setSubmitting(false);
    }
  }, [open]);

  async function handleConfirm() {
    setSubmitting(true);
    setError(null);
    const res = await client.resetUserPassword(email, password);
    setSubmitting(false);
    if (res.ok) {
      onSuccess();
      onOpenChange(false);
    } else {
      setError(res.message ?? t('administration.errors.generic'));
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('administration.reset.title')}</DialogTitle>
          <DialogDescription>{t('administration.reset.body', { email })}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-2">
          <Label htmlFor="reset-password">{t('administration.reset.newPassword')}</Label>
          <Input
            id="reset-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>

        {error && <StatusCard type="error" message={error} />}

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button variant="default" size="sm" onClick={handleConfirm} loading={submitting}>
            {t('administration.reset.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
